import type { ComponentType } from 'react';
import type {
  ActionsEnumKey,
  FeatureFlagControllerAtMeQueryParamsKeyEnumKey,
  Customer,
  GetBrokerageFirmOutputFirmTypeEnumKey,
  PolicyDtoStatusEnumKey,
  UpdateBrokerageFirmInputBody,
} from 'korint-sdk';
import type { FieldShortcut, FormSections, FormSectionsWithShortcuts } from './Forms.types';
import type { InformationFieldParams } from './KorintConfig';
import { IdentityProviders } from './hooks';
import type { CountryCode } from 'libphonenumber-js/max';

export type Language = 'fr' | 'en';

// Known page types that have predefined routing and components
export type PolicyholderPageType =
  | 'home'
  | 'information'
  | 'documents'
  | 'invoices'
  | 'payment-methods';

export type IntermediaryPageType =
  | 'ongoing-new-business'
  | 'archived-new-business'
  | 'portfolio'
  | 'reports'
  | 'commissions'
  | 'intermediary-user-list'
  | 'intermediary-user-form'
  | 'accounting'
  | 'derogations'
  | 'brokerage-firm-list'
  | 'my-brokerage-firm'
  | 'products'
  | 'analytics'
  | 'conversations'
  | 'general-inbox'
  | 'claims'
  | 'fraud-control';

export type PolicyDashboardPageType =
  | 'home'
  | 'informations'
  | 'documents'
  | 'payment-methods'
  | 'invoices'
  | 'events'
  | 'notifications'
  | 'compliance'
  | 'exchanges';

// Base page interface
interface BasePage {
  name: string;
}

interface KnownPolicyholderPage {
  type: PolicyholderPageType;
}

interface KnownIntermediaryPage {
  type: IntermediaryPageType;
  name?: string;
  forTypes?: ReadonlyArray<GetBrokerageFirmOutputFirmTypeEnumKey>;
}

// Custom page type - needs path, icon, and component
interface CustomPage extends BasePage {
  type: 'custom';
  path: '' | `/${string}`;
  icon: React.ReactNode;
  component: React.ReactNode;
}

interface CustomIntermediaryPage extends CustomPage {
  forTypes?: ReadonlyArray<GetBrokerageFirmOutputFirmTypeEnumKey>;
}

interface CustomTab {
  type: 'custom';
  path: '' | `/${string}`;
  component: React.ReactNode;
}

// Union type for all possible pages
export type PolicyholderPage = KnownPolicyholderPage | CustomPage;
export type IntermediaryPage = KnownIntermediaryPage | CustomIntermediaryPage;
export type PolicyDashboardPage = PolicyDashboardPageType | CustomTab;

interface BaseStep {
  path: string;
  hidden?: true;
  /** Icon displayed inside the step circle. Defaults to step number if omitted. */
  icon?: ComponentType<{ className?: string }> | ((props: { className?: string }) => unknown);
  /**
   * When set, automatically pushes this custom event via `policyControllerPushPolicyEvent`
   * after the step completes (e.g. after form submission).
   */
  triggerEvent?: string;
  /**
   * When true, the `triggerEvent` is pushed on every submission, even if the form is pristine.
   * Use this when the step needs to re-fire its event on funnel revisits (e.g. to regenerate
   * downstream documents after upstream changes). Defaults to false (event only fires on dirty).
   */
  triggerAlways?: boolean;
  /**
   * Optional sticky helper panel displayed alongside the step content.
   * Rendered in a two-column layout on xl screens (5/7 content + 2/7 helper).
   * Accepts any React node — use JSX for rich HTML content.
   */
  helper?: React.ReactNode;
}

interface ModuleStepAlert {
  variant?: 'default' | 'info' | 'destructive';
  className?: string;
  icon?: React.ReactNode;
  title?: string;
  description: React.ReactNode;
}

interface ModuleStep extends BaseStep {
  type: 'module';
  module: React.ReactNode;
  alert?: ModuleStepAlert;
  nextButtonText?: string;
  nextButton?: {
    /** React hook, called on every render of the step; Next stays disabled while it returns false. */
    useEnabled?: () => boolean;
  };
  /** Runs when Next is clicked, before moving on; return a branch id if it forked. */
  beforeNext?: (params: {
    policyId: string;
    branchId: string;
  }) => AfterSubmitResult | Promise<AfterSubmitResult>;
}

/** Return the branch id when a mutation inside `afterSubmit` forked, so the funnel follows it. */
export type AfterSubmitResult = void | { branchId?: string };

interface ComplianceStep extends BaseStep {
  type: 'complianceFunnel';
}

interface PolicyStep extends BaseStep {
  type: 'policyFunnel';
  sections: FormSectionsWithShortcuts;
  afterSubmit?: (
    data: Record<string, unknown>,
    params: { customerId: string; policyId: string; branchId: string; isDirty: boolean },
  ) => AfterSubmitResult | Promise<AfterSubmitResult>;
}

interface DynamicStepsStep extends BaseStep {
  type: 'dynamicSteps';
}

interface CustomerStep extends BaseStep {
  type: 'customerFunnel';
  sections: FormSectionsWithShortcuts;
  maxCustomers?: number;
  minCustomers?: number;
  description?: string;
  role?: string;
  addCustomerButtonLabel?: string;
  afterSubmit?: (
    data: Record<string, unknown>,
    params: { customerId: string; policyId: string; branchId: string; isDirty: boolean },
  ) => AfterSubmitResult | Promise<AfterSubmitResult>;
}

interface CompanyFunnelStep extends BaseStep {
  type: 'companyFunnel';
  additionalFields?: FieldShortcut[];
  additionalSections?: FormSectionsWithShortcuts;
  afterSubmit?: (
    data: Record<string, unknown>,
    params: { customerId: string; policyId: string; branchId: string; isDirty: boolean },
  ) => AfterSubmitResult | Promise<AfterSubmitResult>;
}

interface MtaStep extends BaseStep {
  type: 'newMTACreation';
}

interface CustomStep extends BaseStep {
  type: 'custom';
  component: React.ReactNode;
  title?: string;
}

interface IntermediaryFunnelEndStep extends BaseStep {
  type: 'intermediaryFunnelEnd';
}

interface IntermediaryStep extends BaseStep {
  type: 'intermediaryFunnel';
  fields: Array<FieldShortcut>;
}

interface DistributionRequestStep extends BaseStep {
  type: 'distributionRequestFunnel';
}

interface AssetStep extends BaseStep {
  type: 'assetFunnel';
  fields?: FieldShortcut[];
  sections?: FormSectionsWithShortcuts;
  minAssets?: number;
  maxAssets?: number;
  description?: string;
  afterSubmit?: (params: {
    policyId: string;
    branchId: string;
  }) => AfterSubmitResult | Promise<AfterSubmitResult>;
  /**
   * Opt-in: renders a sticky left navigation column (one entry per asset, with
   * the active asset's sections) to jump between assets in a long multi-asset
   * funnel. Off by default so other products/tenants are unaffected.
   */
  showScrollNav?: boolean;
}

interface FleetAssetStep extends BaseStep {
  type: 'fleetAssetFunnel';
  fields?: FieldShortcut[];
  description?: string;
}

interface EmptyAssetStep extends BaseStep {
  type: 'emptyAssetFunnel';
  hidden: true;
}

interface CallToActionCallbackProps {
  policyId: string;
  customer: Customer;
  branchId: string;
}

interface FunnelEndStep extends BaseStep {
  type: 'funnelEnd';
  id: string;
  callback: {
    callToActionCallback?: (props: CallToActionCallbackProps) => Promise<void> | void;
  };
  additionalComponents: {
    isQuoteTierModuleVisible: boolean;
    showDocumentsGeneratedAtQuote?: boolean;
    finishWithoutSendingButton?: {
      enabled?: boolean;
    };
  };
  redirectTo?: (props: CallToActionCallbackProps) => string;
}
interface IntermediarySignatureStep extends BaseStep {
  type: 'intermediarySignatureFunnel';
  displayContext: 'broker' | 'policyholder';
}

interface QuoteStep extends BaseStep {
  type: 'quoteFunnel';
  showQuoteRecap?: 'aside' | 'inline' | false;
  policySections?: FormSectionsWithShortcuts;
  // Policy sections rendered AFTER the options card (delegated quote only). Same persistence path
  // as policySections; use for a policy field conditioned on a selected option (e.g. a killing
  // question shown once its peril is chosen).
  policySectionsAfterOptions?: FormSectionsWithShortcuts;
  assetSections?: FormSectionsWithShortcuts;
  additionalQuoteComponentsSections?: Array<FormSections[number] & { forTypes?: string[] }>;
  manualQuoteSections?: FormSections;
  nonDelegatedDocuments?: string[];
  additionalComponent?: React.ReactNode;
  additionalComponentAfterOptions?: React.ReactNode;
}

interface BillingAssignmentStep extends BaseStep {
  type: 'billingAssignmentFunnel';
  roleIcons?: Record<string, React.ComponentType<{ className?: string }>>;
}

interface BillingSelectionStep extends BaseStep {
  type: 'billingSelectionFunnel';
  frequencyIcons?: Record<string, React.ComponentType<{ className?: string }>>;
  earlyPaymentDisplay?: 'toggle';
}

interface PaymentMethodFunnelStep extends BaseStep {
  type: 'paymentMethodFunnel';
}

interface DocumentUploadFunnelStep extends BaseStep {
  type: 'documentUploadFunnel';
  context: 'policy' | 'brokerageFirm';
}

interface BrokerageFirmUploadDocumentsFunnelValidatedStep extends BaseStep {
  type: 'brokerageFirmUploadDocumentsFunnelValidated';
}

export type FunnelStep =
  | IntermediaryFunnelEndStep
  | ModuleStep
  | ComplianceStep
  | PolicyStep
  | DynamicStepsStep
  | CustomerStep
  | CompanyFunnelStep
  | CustomStep
  | QuoteStep
  | MtaStep
  | IntermediaryStep
  | DistributionRequestStep
  | AssetStep
  | FleetAssetStep
  | EmptyAssetStep
  | FunnelEndStep
  | IntermediarySignatureStep
  | BillingAssignmentStep
  | BillingSelectionStep
  | PaymentMethodFunnelStep
  | DocumentUploadFunnelStep
  | BrokerageFirmUploadDocumentsFunnelValidatedStep;

export interface FunnelStepsConfig {
  steps: readonly [FunnelStep, ...FunnelStep[]];
  afterEndPath?: string;
}

export enum CustomFunnelAuth {
  REQUIRED = 'required',
  ANONYMOUS = 'anonymous',
}

export interface CustomFunnelConfig {
  name: string;
  basePath: string;
  steps: readonly [FunnelStep, ...FunnelStep[]];
  afterEndPath?: string;
  layout?: 'policy' | 'intermediary' | 'policyholder' | 'none' | 'custom' | 'embedded';
  auth?: CustomFunnelAuth;
  /** Every route redirects home while this flag is off. */
  featureFlag?: FeatureFlagControllerAtMeQueryParamsKeyEnumKey;
  /**
   * Optional custom layout configuration for `layout: 'standalone'` funnels.
   * Wraps the funnel with a custom header, footer, and sticky banner.
   */
  customLayout?: {
    /** Raw HTML string injected at the top of the page */
    header?: string;
    /** Raw HTML string injected at the bottom of the page */
    footer?: string;
  };
}

type IntermediaryInformationCardFieldType =
  | 'text'
  | 'number'
  | 'date'
  | 'email'
  | 'tel'
  | 'siret'
  | 'postcode'
  | 'select';

interface IntermediaryInformationCardSingleField {
  kind?: 'single';
  label?: string;
  accessorKey: string;
  append?: string;
  type: IntermediaryInformationCardFieldType;
  key: string;
  allowEdition?: boolean;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  countries?: CountryCode[];
  options?: string[];
  /**
   * Maps the edited value to a PATCH body. Defaults to writing as a customField
   * keyed by `key`. Override when the field targets a structured property
   * (e.g. `companyInfo.location.address`) that the backend deep-merges.
   */
  formDataAccessor?: (value: string | number | boolean) => UpdateBrokerageFirmInputBody;
}

interface IntermediaryInformationCardGroupField {
  kind: 'group';
  key: string;
  label?: string;
  fields: ReadonlyArray<{
    key: string;
    label?: string;
    accessorKey: string;
    type: IntermediaryInformationCardFieldType;
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    countries?: CountryCode[];
  }>;
  /**
   * Maps the edited values to a single composite PATCH body. Required for
   * groups because the backend validates the composite (e.g. address line +
   * postCode + city together) and per-field PATCHes would fail.
   */
  formDataAccessor: (
    values: Record<string, string | number | boolean | undefined>,
  ) => UpdateBrokerageFirmInputBody;
}

type IntermediaryInformationCardModuleField =
  | IntermediaryInformationCardSingleField
  | IntermediaryInformationCardGroupField;

export type PolicyQuickActionEntity = 'policy' | 'asset' | 'customer';

interface BasePolicyQuickAction {
  /** Sole i18n discriminant: every label is read from `policyQuickActions.<id>.*`. */
  id: string;
  icon?: ComponentType<{ className?: string }>;
  forProducts?: ReadonlyArray<string>;
  forStatuses?: ReadonlyArray<PolicyDtoStatusEnumKey>;
  /** Firm types of the users who see the action; super users always do. */
  forFirmTypes?: ReadonlyArray<GetBrokerageFirmOutputFirmTypeEnumKey>;
  /** Shown only while this flag is on for the policy's product. */
  featureFlag?: FeatureFlagControllerAtMeQueryParamsKeyEnumKey;
}

export interface PublishEventQuickAction extends BasePolicyQuickAction {
  type: 'publishEvent';
  /** Must be declared in the product's `customEvents.policy`, or the backend rejects the push. */
  eventType: string;
  data?: Record<string, unknown>;
}

export interface UpdateFieldQuickAction extends BasePolicyQuickAction {
  type: 'updateField';
  entity: PolicyQuickActionEntity;
  fields: ReadonlyArray<string | InformationFieldParams>;
}

export interface CustomQuickAction extends BasePolicyQuickAction {
  type: 'custom';
  /** Every listed action must be granted; the dialog owns any finer-grained check. */
  requiredActions?: ReadonlyArray<ActionsEnumKey>;
  dialog: ComponentType<{ onClose: () => void }>;
}

export interface LinkQuickAction extends BasePolicyQuickAction {
  type: 'link';
  /** In-app route; `:policyId` resolves to the dashboard's policy. */
  path: string;
  /** Every listed action must be granted. */
  requiredActions?: ReadonlyArray<ActionsEnumKey>;
}

export type PolicyQuickAction =
  | PublishEventQuickAction
  | UpdateFieldQuickAction
  | CustomQuickAction
  | LinkQuickAction;

// Configuration type
export interface UniqAppConfig {
  title: string;
  colors: {
    primary: string;
    primaryForeground: string;
    secondary: string;
    secondaryForeground: string;
  };
  /**
   * Path or URL to the logo asset served by the frontend
   */
  logoPath: string;
  /**
   *  Path or URL to the favicon asset served by the frontend
   */
  favicon: string;
  /**
   * The ID of the wholesale brokerage firm.
   */
  parentBrokerageFirmId?: `brokerage_firm_${string}`;
  policyholderDashboard: {
    pages: ReadonlyArray<PolicyholderPage>;
    showTerminationButton?: boolean;
    showPaymentFailureBanner?: boolean;
  };
  intermediaryDashboard: {
    pages: ReadonlyArray<IntermediaryPage>;
    otherPages: ReadonlyArray<Omit<IntermediaryPage, 'forTypes'>>;
    hideNewBusinessButton?: boolean;
  };
  policyDashboard: {
    pages: ReadonlyArray<PolicyDashboardPage>;
    canModifySignedPolicy?: ReadonlyArray<GetBrokerageFirmOutputFirmTypeEnumKey>;
    quickActions?: ReadonlyArray<PolicyQuickAction>;
  };
  intermediaryFunnelSteps?: FunnelStepsConfig;

  intermediaryDetailsPages?: {
    intermediaryInformations?: ReadonlyArray<{
      titleKey: string;
      fields: IntermediaryInformationCardModuleField[];
    }>;
    intermediaryExtraDocuments?: ReadonlyArray<{
      category: string;
    }>;
  };

  login?: {
    providers?: IdentityProviders[];
    showLastLoginHint?: boolean;
    /** Link under the login form, e.g. to a public subscription page. */
    link?: { label: string; to: string };
  };

  customFunnels?: ReadonlyArray<CustomFunnelConfig>;

  /**
   * Languages the tenant officially supports. When this list contains 2 or more
   * entries the `LanguageToggle` is rendered in the UI, letting the user switch
   * between them. When omitted or with a single entry the toggle stays hidden
   * and the app keeps its default language (French).
   */
  availableLanguages?: ReadonlyArray<Language>;
}
